import React, { useMemo, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Form, FormInstance, Switch } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import Checkbox from 'components/lib/Checkbox';
import Input from 'components/lib/Input';
import Loader from 'components/lib/Loader';
import Radio from 'components/lib/Radio';
import SelectAdvancedFilterRadio from 'components/lib/SelectAdvancedFilterRadio';
import TextArea from 'components/lib/TextArea';

import { CmiDigestCommunity } from './types';
import { CMI_DIGEST_FREQUENCY_OPTIONS, SETTINGS_EXTRA_COLUMN_WIDTH, SHOW_RECIPIENTS_OPTIONS } from '../../constants';

export const Body = styled.div`
  display: flex;
  align-items: stretch;
  flex-grow: 1;
  min-height: 0;
`;

export const MainColumn = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0%;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  border-right: 1px solid var(--line-gray);
  padding: 30px;

  &.no-border {
    border-right: none;
    padding-top: 5px;
    padding-right: 20px;
  }
`;

export const SideColumn = styled.div`
  width: ${SETTINGS_EXTRA_COLUMN_WIDTH}px;
  flex-shrink: 0;
  padding: 30px;
  display: flex;
  flex-direction: column;
  min-height: 0;

  &.no-border {
    padding-top: 5px;
    padding-left: 20px;
  }
`;

export const EnableRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 20px;

  &.no-border {
    padding-bottom: 30px;
  }
`;

export const StyledSwitch = styled(Switch)`
  &.ant-switch-checked {
    background: var(--turquoise);

    &:hover:not(.ant-switch-disabled) {
      background: var(--turquoise);
    }
  }
`;

export const EnableLabel = styled.span`
  font-family: var(--font-bold);
  font-size: 14px;
`;

export const FrequencyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const Switcher = styled.div`
  padding: 30px;
  border-bottom: 1px solid var(--line-gray);
  border-top: 1px solid var(--line-gray);

  &.no-border {
    border-top: none;
    border-bottom: none;
    padding: 0 0 30px;
  }
`;

export const FrequencyLabel = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

export const SectionTitle = styled.div`
  font-size: 14px;
  margin-bottom: 20px;
`;

export const StyledCheckbox = styled(Checkbox)`
  font-size: 14px;

  &&.ant-checkbox-wrapper {
    align-items: center;
  }

  .ant-checkbox-label {
    display: flex;
    align-items: center;
  }
`;

export const Card = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  border: 1px solid var(--line-gray);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 12px;

  &.no-border {
    padding: 20px;
  }
`;

export const AiInstructionsCard = styled(Card)`
  flex-direction: column;
  align-items: stretch;
`;

export const AiInstructionsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

export const AiInstructionsLabel = styled.span`
  font-family: var(--font-bold);
  font-size: 14px;
`;

export const AiTag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 16px;
  padding: 2px 8px;
  color: var(--hot-pink);
  background: #ffe0ef;
  font-family: var(--font-bold);
  font-size: 12px;
  line-height: 1;
  border-radius: 4px;
`;

export const AiInstructionsDescription = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  margin-bottom: 10px;
`;

export const StyledDisplayFilter = styled(SelectAdvancedFilterRadio)`
  margin-bottom: 16px;
`;

export const CommunitiesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

export const CommunitiesTitle = styled.span`
  font-family: var(--font-bold);
  font-size: 16px;
`;

export const CommunitiesList = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;

  flex-grow: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const CommunityRow = styled.div`
  padding: 8px 0;
`;

export const EmptyMessage = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  padding: 12px 0;
`;

export const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

type Props = {
  form: FormInstance;
  communities: CmiDigestCommunity[];
  isLoadingCommunities: boolean;
  hideDividers?: boolean;
};

// Renders nothing - antd's Form.Item clones its child with value/onChange (and a ref), so this
// exists purely to give Form.Item something to wrap and register "communities" as a real field.
const CommunitiesFieldRegistration = React.forwardRef(() => null);

const CmiDigestForm = ({ form, communities, isLoadingCommunities, hideDividers }: Props) => {
  const [search, setSearch] = useState('');
  const [displayFilter, setDisplayFilter] = useState<number>(SHOW_RECIPIENTS_OPTIONS[0].value as number);

  const selectedCommunityIds: number[] = Form.useWatch('communities', form) ?? [];
  const isEnabled: boolean = Form.useWatch('enabled', form) ?? false;

  const filteredCommunities = useMemo(() => {
    return communities.filter((community) => {
      const matchesSearch = community.comunity_name.toLowerCase().includes(search.toLowerCase());
      const matchesFilter = displayFilter === 0 || selectedCommunityIds.includes(community.id);
      return matchesSearch && matchesFilter;
    });
  }, [communities, search, displayFilter, selectedCommunityIds]);

  const allFilteredSelected =
    filteredCommunities.length > 0 &&
    filteredCommunities.every((community) => selectedCommunityIds.includes(community.id));

  const toggleSelectAll = (checked: boolean) => {
    const filteredIds = filteredCommunities.map((community) => community.id);
    const nextSelectedIds = checked
      ? Array.from(new Set([...selectedCommunityIds, ...filteredIds]))
      : selectedCommunityIds.filter((id) => !filteredIds.includes(id));
    form.setFieldsValue({ communities: nextSelectedIds });
  };

  const toggleCommunity = (communityId: number, checked: boolean) => {
    const nextSelectedIds = checked
      ? Array.from(new Set([...selectedCommunityIds, communityId]))
      : selectedCommunityIds.filter((id) => id !== communityId);
    form.setFieldsValue({ communities: nextSelectedIds });
  };

  const handleEnabledChange = (checked: boolean) => {
    if (!checked) {
      form.setFieldsValue({
        communities: [],
        competitor_incentives_enabled: false,
        community_reviews_enabled: false,
      });
    }
  };

  const switcherContent = (
    <>
      <EnableRow className={hideDividers ? 'no-border' : undefined}>
        <Form.Item name="enabled" valuePropName="checked" noStyle>
          <StyledSwitch onChange={handleEnabledChange} data-test-id="cmi-digest-enable-toggle" />
        </Form.Item>
        <EnableLabel>Enable CMI Digest</EnableLabel>
      </EnableRow>
      <FrequencyRow>
        <FrequencyLabel>Frequency</FrequencyLabel>
        <Form.Item name="frequency" noStyle>
          <Radio.Group disabled={!isEnabled}>
            {CMI_DIGEST_FREQUENCY_OPTIONS.map((option) => (
              <Radio key={option.value} value={option.value} data-test-id={`cmi-digest-frequency-${option.value}`}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      </FrequencyRow>
    </>
  );

  return (
    <>
      <Form.Item name="communities" noStyle>
        <CommunitiesFieldRegistration />
      </Form.Item>
      {!hideDividers && <Switcher>{switcherContent}</Switcher>}
      <Body>
        <MainColumn className={classNames('mini-scroll', { 'no-border': hideDividers })}>
          {hideDividers && <Switcher className="no-border">{switcherContent}</Switcher>}
          <SectionTitle>Content Included</SectionTitle>
          <Card className={hideDividers ? 'no-border' : undefined}>
            <Form.Item name="competitor_incentives_enabled" valuePropName="checked" noStyle>
              <StyledCheckbox
                className="green"
                disabled={!isEnabled}
                data-test-id="cmi-digest-content-competitor-incentives"
              >
                Competitor Incentives
              </StyledCheckbox>
            </Form.Item>
          </Card>
          <Card className={hideDividers ? 'no-border' : undefined}>
            <Form.Item name="community_reviews_enabled" valuePropName="checked" noStyle>
              <StyledCheckbox
                className="green"
                disabled={!isEnabled}
                data-test-id="cmi-digest-content-community-reviews"
              >
                My Community Reviews
              </StyledCheckbox>
            </Form.Item>
          </Card>
          <AiInstructionsCard className={hideDividers ? 'no-border' : undefined}>
            <AiInstructionsHeader>
              <AiInstructionsLabel>AI Instructions</AiInstructionsLabel>
              <AiTag>AI</AiTag>
            </AiInstructionsHeader>
            <AiInstructionsDescription>
              Give instructions of an AI agent on what to prioritize or additionally tweak your needs.
            </AiInstructionsDescription>
            <Form.Item name="ai_instructions" noStyle>
              <TextArea
                rows={4}
                disabled={!isEnabled}
                placeholder="e.g. Negative reviews about care or staffing matter most to us, or flag incentives like a waived community fee or more than one month free — not just any promotion."
                data-test-id="cmi-digest-ai-instructions-textarea"
              />
            </Form.Item>
          </AiInstructionsCard>
        </MainColumn>

        <SideColumn className={hideDividers ? 'no-border' : undefined}>
          <StyledDisplayFilter
            options={SHOW_RECIPIENTS_OPTIONS}
            defaultValue={displayFilter}
            onSelect={setDisplayFilter}
            placeholder="Display"
            dataTestId="cmi-digest-communities-display-filter"
          />

          <CommunitiesHeader>
            <StyledCheckbox
              className="green"
              checked={allFilteredSelected}
              disabled={!isEnabled}
              onChange={(e: any) => toggleSelectAll(e.target.checked)}
              data-test-id="cmi-digest-communities-select-all"
            >
              <CommunitiesTitle>Communities included</CommunitiesTitle>
            </StyledCheckbox>
          </CommunitiesHeader>

          <Input
            prefix={<SearchOutlined />}
            placeholder="Search communities"
            value={search}
            onChange={(e: any) => setSearch(e.target.value)}
            style={{ marginBottom: 12 }}
            data-test-id="cmi-digest-communities-search-input"
          />

          <CommunitiesList className="mini-scroll">
            {isLoadingCommunities ? (
              <LoaderWrapper>
                <Loader active />
              </LoaderWrapper>
            ) : filteredCommunities.length === 0 ? (
              <EmptyMessage>No communities found</EmptyMessage>
            ) : (
              filteredCommunities.map((community) => (
                <CommunityRow key={community.id}>
                  <StyledCheckbox
                    className="green"
                    checked={selectedCommunityIds.includes(community.id)}
                    disabled={!isEnabled}
                    onChange={(e: any) => toggleCommunity(community.id, e.target.checked)}
                    data-test-id={`cmi-digest-community-checkbox-${community.id}`}
                  >
                    {community.comunity_name}
                  </StyledCheckbox>
                </CommunityRow>
              ))
            )}
          </CommunitiesList>
        </SideColumn>
      </Body>
    </>
  );
};

export default CmiDigestForm;
